import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '@clatter/platform';
import { Loader, useDocumentTitle } from '@clatter/ui';
import {
  fetchMicrosites,
  fetchPageTemplates,
  fetchPages,
  fetchHeroImages,
  fetchVideoEmbeds,
} from '../../store';
import Page from "../../components/Page/Page";
import { pageFromStore, heroImageFromStore } from '../fromStore';
import SiteMaker from '../../components/SiteMaker/SiteMaker';
import PageSettingsForm from '../../components/PageSettingsForm/PageSettingsForm';
import { selectAllVideoEmbeds } from '../../store/video-embeds.slice';
import { selectAllPageTemplates } from '../../store/page-templates.slice';
import useMsmPageTitle from "../../hooks/useMsmPageTitle";
import { selectPagesEntities } from '../../store/pages.slice';

const PageSettings = () => {
  // set page title for GA4
  useMsmPageTitle()
  const { user, isLoading } = useAuth();
  let { siteId, pageId } = useParams();

  // parse params
  pageId = parseInt(pageId);

  if (isLoading) {
    return <div>Loading authorization...</div>;
  }

  const dispatch = useDispatch();
  useEffect(() => {
    // not site/page dependent fetches
    dispatch(fetchPageTemplates());
    dispatch(fetchHeroImages());
    dispatch(fetchVideoEmbeds());

    if (!siteId) {
      return false;
    }

    (async () => {
      const microsites = await dispatch(fetchMicrosites({ user: user, micrositeId: siteId }));
      const micrositePagesIds = (microsites?.payload?.data?.[0]?.attributes?.pages?.data || []).map(page => page?.id);

      dispatch(fetchPages({ pagesIds: micrositePagesIds }));
    })();

    window.scrollTo(0, 0)
  }, [siteId, user]);

  const loading = useSelector(
    (state) =>
      state.pageTemplates.loadingStatus === 'loading' ||
      state.microsites.loadingStatus === 'loading' ||
      state.pages.loadingStatus === 'loading' ||
      state.heroImages.loadingStatus === 'loading' ||
      state.videoEmbeds.loadingStatus === 'loading',
  );

  const pageTemplates = useSelector(selectAllPageTemplates);

  const heroImages = useSelector((state) =>
    Object.values(state.heroImages.entities).map(heroImageFromStore),
  );

  const pageEntities = useSelector(selectPagesEntities);
  const videos = useSelector(selectAllVideoEmbeds);

  const currentMicrosite = useSelector((state) => {
    if (state.pages.ids.indexOf[pageId] === -1) {
      return;
    }

    const siteId = state.microsites.ids.find((micrositeId) =>
      state.microsites.entities[micrositeId].pages.some(
        (page) => page.id === pageId,
      ),
    );

    if (!siteId || !state.microsites.entities[siteId]) {
      return;
    }

    return {
      ...state.microsites.entities[siteId],
      pages: state.microsites.entities[siteId].pages?.map((page) =>
        pageFromStore(siteId, page, pageTemplates, pageEntities[page.id]),
      ),
    };
  });

  const currentPage = currentMicrosite?.pages?.find(
    (page) => page?.id === pageId,
  );

  if (
    !loading &&
    (!currentMicrosite || !currentMicrosite.pages || !currentPage)
  ) {
    return <p>Couldn't load current page</p>;
  }

  return (
    <Page key={pageId}>
      {loading && <Loader />}
      <SiteMaker site={currentMicrosite}>
        <PageSettingsForm
          currentMicrosite={currentMicrosite}
          currentPage={currentPage}
          heroImages={heroImages}
          pageId={pageId}
          videos={videos}
        />
      </SiteMaker>
    </Page>
  );
};

export default PageSettings;
